import React from 'react';
import { useAppUser, useWorkPackageOwner } from '@bechtel/client-common';
import { CrewMemberAssignment, AuthPermission, AuthScope } from '@bechtel/types';
import { some, includes, map } from 'lodash';
import { useWorkPackage } from '~/detail/context';

export function useWorkPackagePermissions(assignedCrewMembers?: CrewMemberAssignment[]) {
  const { facilities: workPackageFacilities } = useWorkPackage();
  const { hasPermissionScope, user } = useAppUser();
  const userFacilities = user?.facilities ? user?.facilities : [];
  const isOwner = useWorkPackageOwner(assignedCrewMembers);
  const isFacilityOwner =
    !workPackageFacilities || workPackageFacilities.length == 0 || !userFacilities || userFacilities.length == 0
      ? false
      : some(workPackageFacilities, (wpf) =>
          includes(
            map(userFacilities, (userFacility) => userFacility.id),
            wpf.id
          )
        );

  const workPackagePermissions = React.useMemo(() => {
    const hasWorkPackagePermissionScope = (permission: AuthPermission) =>
      (isOwner && hasPermissionScope({ permission: permission, scope: AuthScope.MY_WORK_PACKAGES })) ||
      (isFacilityOwner && hasPermissionScope({ permission: permission, scope: AuthScope.MY_AREA })) ||
      hasPermissionScope({ permission: permission, scope: AuthScope.ALL_WORK_PACKAGES });

    return {
      canClaimMilestone: hasWorkPackagePermissionScope(AuthPermission.CLAIM_UNCLAIM_MILESTONE),
      canApproveMilestone: hasWorkPackagePermissionScope(AuthPermission.APPROVE_UNAPPROVE_MILESTONE),
      canRejectMilestone: hasWorkPackagePermissionScope(AuthPermission.REJECT_MILESTONE),
      canEditForeman: hasWorkPackagePermissionScope(AuthPermission.EDIT_FOREMAN_WORK_PACKAGE),
      canEditGeneralForeman: hasWorkPackagePermissionScope(AuthPermission.EDIT_GENERAL_FOREMAN_WORK_PACKAGE),
      canEditSuperintendent: hasWorkPackagePermissionScope(AuthPermission.EDIT_SUPERINTENDENT_WORK_PACKAGE),
      canEditFieldEngineer: hasWorkPackagePermissionScope(AuthPermission.EDIT_FIELD_ENGINEER_WORK_PACKAGE),
      canEditWeldForeman: hasWorkPackagePermissionScope(AuthPermission.ASSIGN_UNASSIGN_WELD_FOREMAN_COMMODITY),
      canEditCrewTime: hasWorkPackagePermissionScope(AuthPermission.CREATE_BLOCK),
      canEditSchedule: hasWorkPackagePermissionScope(AuthPermission.EDIT_SCHEDULE_WORK_PACKAGE),
      canUploadDocument: hasWorkPackagePermissionScope(AuthPermission.WORK_PACKAGE_DOCUMENT_UPLOAD),
      canClaimWeld: hasWorkPackagePermissionScope(AuthPermission.CLAIM_UNCLAIM_WELD),
      canCreateFitupRequest: hasWorkPackagePermissionScope(AuthPermission.REQUEST_FITUP),
      canDeleteDocument: hasWorkPackagePermissionScope(AuthPermission.DELETE_WORK_PACKAGE_DOCUMENT),
      canDeleteMilestoneAttachment: hasWorkPackagePermissionScope(AuthPermission.DELETE_ATTACHMENT_MILESTONE),
    };
  }, [hasPermissionScope, isOwner]);

  return workPackagePermissions;
}
